import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import type { CloudResource, CloudResourceInsert, CloudResourceUpdate } from '@/types/cloudResource';

interface CloudResourceState {
  cloudResources: CloudResource[];
  loading: boolean;
  error: string | null;
  
  // Actions
  loadCloudResources: () => Promise<void>;
  addCloudResource: (resource: CloudResourceInsert) => Promise<CloudResource | null>;
  updateCloudResource: (id: string, updates: CloudResourceUpdate) => Promise<boolean>;
  deleteCloudResource: (id: string) => Promise<boolean>;
  getCloudResourceById: (id: string) => CloudResource | undefined;
  getCloudResourcesByServiceId: (cloudServiceId: string) => CloudResource[];
}

export const useCloudResourceStore = create<CloudResourceState>((set, get) => ({
  cloudResources: [],
  loading: false,
  error: null,

  loadCloudResources: async () => {
    set({ loading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        set({ loading: false, error: 'Not authenticated' });
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!profile?.org_id) {
        set({ loading: false, cloudResources: [] });
        return;
      }

      // Use type assertion since cloud_resources is new and not in generated types yet
      const { data, error } = await (supabase
        .from('cloud_resources' as any)
        .select('*')
        .eq('org_id', profile.org_id)
        .order('name')) as { data: CloudResource[] | null; error: any };

      if (error) throw error;
      set({ cloudResources: data || [], loading: false });
    } catch (error: any) {
      console.error('Error loading cloud resources:', error);
      set({ error: error.message, loading: false });
    }
  },

  addCloudResource: async (resource: CloudResourceInsert) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!profile?.org_id) return null;

      const insertData = {
        ...resource,
        org_id: profile.org_id,
        owner_id: resource.owner_id || null,
      };

      const { data, error } = await (supabase
        .from('cloud_resources' as any)
        .insert(insertData)
        .select()
        .single()) as { data: CloudResource | null; error: any };

      if (error) throw error;
      if (data) {
        set(state => ({ cloudResources: [...state.cloudResources, data] }));
      }
      return data;
    } catch (error: any) {
      console.error('Error adding cloud resource:', error);
      return null;
    }
  },

  updateCloudResource: async (id: string, updates: CloudResourceUpdate) => {
    try {
      const updateData = {
        ...updates,
        owner_id: updates.owner_id === undefined ? undefined : (updates.owner_id || null),
      };
      const { error } = await (supabase
        .from('cloud_resources' as any)
        .update(updateData)
        .eq('id', id)) as { error: any };

      if (error) throw error;

      set(state => ({
        cloudResources: state.cloudResources.map(r =>
          r.id === id ? { ...r, ...updates, updated_at: new Date().toISOString() } : r
        )
      }));
      return true;
    } catch (error: any) {
      console.error('Error updating cloud resource:', error);
      return false;
    }
  },

  deleteCloudResource: async (id: string) => {
    try {
      const { error } = await (supabase
        .from('cloud_resources' as any)
        .delete()
        .eq('id', id)) as { error: any };

      if (error) throw error;

      set(state => ({
        cloudResources: state.cloudResources.filter(r => r.id !== id)
      }));
      return true;
    } catch (error: any) {
      console.error('Error deleting cloud resource:', error);
      return false;
    }
  },

  getCloudResourceById: (id: string) => {
    return get().cloudResources.find(r => r.id === id);
  },

  getCloudResourcesByServiceId: (cloudServiceId: string) => {
    return get().cloudResources.filter(r => r.cloud_service_id === cloudServiceId);
  },
}));
